import { queryOptions } from '@tanstack/react-query';

import { apiGet, type ApiError, type QueryOf, type ResponseOf } from './http';
import { queryKeys } from './queryKeys';

/**
 * `GET /api/v1/dashboard/outstanding/` — §6.2 Section 1's list with the cap taken off (backend
 * TASK-156, client TASK-157).
 *
 * **It is the same query Section 1 runs, not a second one.** The route builds its rows through the
 * dashboard service's own `_outstanding_drafts` / `_oldest_row`, so the first page of this list *is*
 * Section 1's five rows, field for field and in the same order — which is why the screen this feeds
 * reuses Section 1's columns rather than describing the row again.
 *
 * ⚠️ **Not `GET /api/v1/interviews/?submitted=false`, and the reason is measured rather than
 * preferred.** That route answers a colleague's draft as the **blinded** shape — no `consideration`
 * object, therefore no candidate name and no vacancy (ADR 0006) — so it structurally cannot serve a
 * `Recruiter` or an `Admin` this screen, and widening it would be widening the blind gate. A count of
 * conversations that are owed is metadata, which is the dashboard's argument, so the address is the
 * dashboard's too.
 *
 * **Metadata only**: a row is an interview id, three names, a date and an age. No rating, no comment,
 * no recommendation, no snapshot — there is nothing here for ADR 0006 to gate, and the interview
 * address the row links to applies the gate on its own.
 */

/** `?page=` and `?page_size=`, read off the schema rather than restated. */
export type OutstandingListQuery = NonNullable<QueryOf<'/api/v1/dashboard/outstanding/', 'get'>>;

export type OutstandingPage = ResponseOf<'/api/v1/dashboard/outstanding/', 'get'>;

/**
 * One outstanding interview, **the generated `OldestOutstandingRow`** — the very type §6.2 Section 1
 * renders (`Dashboard['outstanding_feedback']['oldest'][number]`), because the backend reuses the
 * schema rather than declaring a second one. Nothing narrows it here: every field is a string, a
 * number or a date the document declares outright.
 */
export type OutstandingRow = OutstandingPage['items'][number];

/**
 * The page size this screen paginates by, **stated rather than inherited** — §6.3's decision, reused.
 *
 * The backend's own default is 50 (`apps/dashboard/api.py`'s `DEFAULT_PAGE_SIZE`) and `?page_size=` is
 * clamped at 100 (`NINJA_MAX_PER_PAGE_SIZE`). *"Showing 1–50 of 137"* is arithmetic over the page
 * size, so the client sends the size it does that arithmetic with instead of assuming the server's
 * default matches: a default changed on the backend would otherwise silently misnumber the range
 * rather than break anything visible.
 */
export const OUTSTANDING_PAGE_SIZE = 50;

/**
 * ⚠️ **The viewer is in the key and no placeholder may reach around it** — the rule
 * `considerationsQuery` records at length. This route is **narrowed** per caller, not refused: an
 * `Interviewer` is answered their own outstanding drafts and a `Recruiter`/`Admin` the tenant's, from
 * the same URL and with the same `200`. One shared entry would be one caller's list served to another.
 */
export function outstandingQuery(viewerId: number, page: number) {
  const query: OutstandingListQuery = { page, page_size: OUTSTANDING_PAGE_SIZE };

  return queryOptions<OutstandingPage, ApiError>({
    queryKey: queryKeys.outstanding(viewerId, query),
    queryFn: () => apiGet('/api/v1/dashboard/outstanding/', { query }),
  });
}
